/* eslint-disable no-inline-styles/no-inline-styles */
import { builder } from '@builder.io/sdk';
import { GetServerSideProps } from 'next';
import dynamic from 'next/dynamic';
import { FooterVariant, HeaderVariant, PageProps } from 'types/global';

import LandingLayout from 'layout/Landing/index';

import { AbAntiFlickerGate, GATE_CLASS, useStickyAbVariant } from 'utils/builder/StickyAbVariant';
import { fetchLayoutModels } from 'utils/builder/builder.sdk';
import derivePageChrome from 'utils/builder/derivePageChrome';
import { primeAboveFoldSwitchback } from 'utils/builder/primeAboveFoldSwitchback';
import { stripPageData } from 'utils/builder/stripPageData';

const ComponentGenerator = dynamic(() => import('utils/builder/ComponentGenerator'));
const RenderBuilderComponentsLanding = dynamic(() =>
  import('utils/builder/RenderBuilderContent').then(mod => ({ default: mod.RenderBuilderComponentsLanding })),
);

builder.init(process.env.NEXT_PUBLIC_BUILDER_API_KEY || '');

const MODEL = 'landing-page';
const PATH = 'lp';

type LPProps = PageProps & {
  headerVariant?: HeaderVariant;
  footerVariant?: FooterVariant;
};

const LandingPage = ({ pageData, layoutData, preview, headerVariant, footerVariant }: LPProps) => {
  // The A/B variant is resolved on the CLIENT (see StickyAbVariant): behind
  // CloudFront the server has no stable per-visitor signal, so it renders the
  // parent and ships every variation, and this hook swaps in the visitor's sticky
  // variant before paint and publishes it to the store for attribution + gating.
  const { activeContent, activeData } = useStickyAbVariant(pageData);

  // Chrome is derived from the ACTIVE variant so an immersive variation gets the
  // right header/footer once the client resolves it.
  const { headerVariant: resolvedHeaderVariant, footerVariant: resolvedFooterVariant } = activeData
    ? derivePageChrome(activeData)
    : { headerVariant, footerVariant };

  if (!activeData) {
    return (
      <LandingLayout
        preview={preview}
        seoData={activeData}
        headerVariant={resolvedHeaderVariant}
        footerVariant={resolvedFooterVariant}
        {...layoutData}
      >
        <div style={{ minHeight: '100vh' }} />
      </LandingLayout>
    );
  }

  return (
    <LandingLayout
      preview={preview}
      seoData={activeData}
      headerVariant={resolvedHeaderVariant}
      footerVariant={resolvedFooterVariant}
      {...layoutData}
    >
      <AbAntiFlickerGate pageData={pageData} />
      <ComponentGenerator>
        <div className={GATE_CLASS}>
          <RenderBuilderComponentsLanding model={MODEL} content={activeContent} />
        </div>
      </ComponentGenerator>
    </LandingLayout>
  );
};

export const getServerSideProps: GetServerSideProps = async ({ params, res, query, preview: draftPreview }) => {
  const slug = Array.isArray(params?.slug) ? params?.slug.join('/') : params?.slug || '';
  const preview = process.env.BUILDER === '1';
  // BUILDER=1 lives on production env too, so use Builder editor query params
  // to decide whether to allow content-less pages through (only for editing).
  const isBuilderEditor = !!query['builder.space'] || !!query['builder.preview'];
  const urlPath = `/${PATH}/${slug}`;

  // The SDK short-circuits A/B selection on the server, so the fetch returns the
  // full content INCLUDING its `variations`. The variant is chosen on the CLIENT
  // (see StickyAbVariant) because CloudFront leaves no stable per-visitor signal
  // at the origin, so for A/B pages we KEEP `variations` in the payload for the
  // browser to pick from; non-A/B pages still drop it. We intentionally do NOT
  // pass a `fields` filter here: filtering to specific fields returns the
  // variations' data in a shape that fails to parse, so fetch the whole content.
  //
  // includeRefs: true inlines referenced content (forms, etc.) into pageData
  // so components like LandingPageHero/ConversionPanel/FreeTrialFTUX read
  // form.value.data from SSR instead of firing per-visit client-side
  // useBuilderReference fetches against cdn.builder.io.
  // The page fetch and the layout models are independent; fetching them
  // sequentially added a full upstream round trip to TTFB on every
  // edge-cache miss.
  const [pageData, layoutData] = await Promise.all([
    builder
      .get(MODEL, {
        userAttributes: { urlPath },
        prerender: false,
        options: {
          noTraverse: !preview,
          includeRefs: true,
        },
        // Only serve unpublished drafts to the Builder.io editor iframe. BUILDER=1
        // is set on the production env too, so `preview` is true for real visitors
        // and keying includeUnpublished on it leaked never-published (and
        // un-published) landing pages at their real URL. isBuilderEditor is the
        // query-param signal that actually distinguishes editing from a visitor.
        includeUnpublished: isBuilderEditor,
      })
      .toPromise(),
    // Next.js Preview Mode (context.preview, cookie-gated via
    // /api/draft-preview) swaps the chrome for its Builder drafts. Page content
    // keeps its own isBuilderEditor gate above; this only affects the chrome.
    fetchLayoutModels(preview, !!draftPreview),
  ]);

  // builder.get() can resolve to an empty object (not null) when no content
  // matches the urlPath, so check for the actual page payload to trigger 404.
  // Skip 404 only when the request is from the Builder.io editor iframe so
  // editors can author new pages that have no published content yet.
  if (process.env.NODE_ENV === 'production' && !pageData?.data && !isBuilderEditor) {
    return { notFound: true };
  }

  // A/B test present when the parent testRatio is < 1; the variant is chosen on
  // the client (see StickyAbVariant).
  const testRatio = (pageData as { testRatio?: number } | null | undefined)?.testRatio;
  const isAbTestActive = typeof testRatio === 'number' && testRatio < 1;

  // The server always renders the parent; the client swaps in the visitor's
  // sticky variant. Chrome is derived from the parent for SSR.
  const resolvedContent = pageData;
  const { headerVariant, footerVariant } = derivePageChrome(resolvedContent?.data);

  // Cache header strategy:
  //
  // - `Cache-Control: private, no-cache, must-revalidate` is sent downstream
  //   (browsers, the CloudFront layer in front of Vercel that trapped Bru's
  //   republishes when cfd16419 used s-maxage on this header). no-cache still
  //   forces revalidation on every normal navigation, so editor publishes
  //   propagate immediately to clients. `no-store` was dropped on purpose: it is
  //   the one directive that disqualifies the page from the browser back/forward
  //   cache, and bfcache restores count as near-instant LCP/INP in CrUX field
  //   data (which feeds the Google Ads landing page experience signal).
  //
  // - `CDN-Cache-Control` is honored only by Vercel's own edge cache and is
  //   stripped from the response before it reaches downstream caches.
  //
  // - A/B pages now render the parent for every visitor and resolve the variant on
  //   the client, so the HTML is identical per request and can be edge-cached like
  //   any other page. (It was previously `no-store` to avoid replaying a
  //   server-picked variant, which no longer applies.)
  res.setHeader('Cache-Control', 'private, no-cache, must-revalidate');
  // Draft-preview responses carry unpublished chrome, so they must never seed
  // the shared Vercel edge cache (same reason as the Builder editor gate).
  if (!isBuilderEditor && !draftPreview) {
    res.setHeader('CDN-Cache-Control', 'public, s-maxage=300, stale-while-revalidate=86400');
  }

  // Defense in depth for noindex pages: some crawlers honor the X-Robots-Tag
  // response header but not the <meta name="robots"> tag in layout/Head. Mirror
  // that tag from the same data.noIndex Builder field, from the resolved variation.
  if (resolvedContent?.data?.noIndex) {
    res.setHeader('X-Robots-Tag', 'noindex, nofollow');
  }

  return {
    props: {
      // Prime the first above-the-fold Switchback image as the LCP element.
      // Runs on the stripped clone so it never mutates the SDK's fetch result. A/B
      // pages KEEP `variations` (keepVariations) for the client to render the
      // sticky variant; non-A/B pages drop it to stay slim.
      pageData: primeAboveFoldSwitchback(stripPageData(resolvedContent, { keepVariations: isAbTestActive })) || {},
      // Also strip Builder editor metadata from header/footer/announcementBar
      // payloads. They get serialized into __NEXT_DATA__ on every page so
      // dropping their meta/screenshot/createdBy etc. is a per-route saving.
      // LandingLayout never renders the announcement bar (it only appears in
      // GlobalLayout), so we additionally drop announcementBarContent from
      // the payload on /lp/* routes to save the ~2 KB it would serialize.
      layoutData: (() => {
        const stripped = stripPageData(layoutData) || ({} as Record<string, unknown>);
        const { announcementBarContent: _ignored, ...rest } = stripped as Record<string, unknown>;

        return rest;
      })(),
      preview: !!preview,
      headerVariant,
      footerVariant,
    },
  };
};

export default LandingPage;
